import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { btn } from "@/components/common/btn";
import { shortDate } from "@/lib/format";
import { getClientFiles, uploadClientFile, deleteClientFile, clientFileUrl } from "@/lib/api";

const TYPES = ["COI", "Tax Certificate", "Contract", "Other"];
const TYPE_PILL = {
  "COI": "bg-sky-100 text-sky-700 border-sky-200",
  "Tax Certificate": "bg-amber-100 text-amber-700 border-amber-200",
  "Contract": "bg-violet-100 text-violet-700 border-violet-200",
  "Other": "bg-slate-100 text-slate-600 border-slate-200",
};
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt";
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const expiryTone = (d) => {
  if (!d) return "";
  const days = Math.round((new Date(d + "T00:00:00") - new Date()) / 86400000);
  return days < 0 ? "text-red-600 font-semibold" : days <= 30 ? "text-amber-600 font-semibold" : "text-slate-500";
};

// Uploaded customer files (COIs, tax certs, contracts) — lives at the top of the History › Documents section.
export function ClientFilesPanel({ clientId, canWrite = true, search = "" }) {
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [docType, setDocType] = useState("COI");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => { if (clientId) getClientFiles(clientId).then((r) => setFiles(r.data)).catch(() => {}); }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!picked.length) { toast.error("Choose at least one file"); return; }
    setBusy(true);
    let ok = 0;
    for (const f of picked) {
      const fd = new FormData();
      fd.append("file", f); fd.append("doc_type", docType); if (expires) fd.append("expires_on", expires); fd.append("note", note);
      try { await uploadClientFile(clientId, fd); ok += 1; }
      catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false);
    if (ok) { toast.success(`${ok} file${ok === 1 ? "" : "s"} attached`); setOpen(false); setPicked([]); setExpires(""); setNote(""); load(); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.original_filename}"?`)) return;
    try { await deleteClientFile(clientId, f.id); setFiles((prev) => prev.filter((x) => x.id !== f.id)); toast.success("File removed"); }
    catch { toast.error("Could not remove the file"); }
  };

  const q = search.trim().toLowerCase();
  const rows = q ? files.filter((f) => [f.original_filename, f.doc_type, f.note, f.uploaded_by].some((v) => (v || "").toLowerCase().includes(q))) : files;

  return (
    <div className="space-y-2" data-testid="client-files-panel">
      <div className="flex items-center gap-2">
        <h5 className="text-xs font-semibold text-slate-500">Uploaded files</h5>
        {canWrite && (
          <button type="button" onClick={() => setOpen(true)} title="Attach a file (COI, tax certificate, contract…)" data-testid="btn-upload-client-file"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
        <span className="ml-auto text-xs text-slate-500 num">{rows.length} file{rows.length === 1 ? "" : "s"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 border border-dashed rounded-lg py-4 text-center" data-testid="client-files-empty">
          {q ? "No uploaded files match that search" : "No files attached yet — COIs, tax certificates and contracts go here."}
        </div>
      ) : (
        <div className="data-table-container">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="th-std w-32">Type</TableHead>
                <TableHead className="th-std">File</TableHead>
                <TableHead className="th-std w-24">Expires</TableHead>
                <TableHead className="th-std w-40">Uploaded</TableHead>
                <TableHead className="th-std w-20 text-right">Size</TableHead>
                <TableHead className="th-std w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.id} className="table-row-hover" data-testid={`client-file-row-${f.id}`}>
                  <TableCell className="text-xs"><span className={`inline-flex w-[88px] justify-center rounded-md border px-2 py-0.5 text-[10px] font-semibold ${TYPE_PILL[f.doc_type] || TYPE_PILL.Other}`}>{f.doc_type}</span></TableCell>
                  <TableCell className="text-xs">
                    <a href={clientFileUrl(clientId, f.id, true)} target="_blank" rel="noopener noreferrer" className="font-medium text-brand hover:underline" data-testid={`client-file-open-${f.id}`}>{f.original_filename}</a>
                    {f.note && <div className="text-[11px] text-slate-500 truncate max-w-[320px]">{f.note}</div>}
                  </TableCell>
                  <TableCell className={`text-xs tabular-nums ${expiryTone(f.expires_on)}`}>{f.expires_on ? shortDate(f.expires_on) : "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{shortDate(f.created_at)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ""}</TableCell>
                  <TableCell className="text-xs text-slate-500 num">{fmtSize(f.size || 0)}</TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center justify-end gap-1">
                      <a href={clientFileUrl(clientId, f.id, true)} target="_blank" rel="noopener noreferrer" className={btn("nav")} title="Preview"><Eye className="w-3.5 h-3.5" /></a>
                      <a href={clientFileUrl(clientId, f.id)} className={btn("nav")} title="Download" data-testid={`client-file-download-${f.id}`}><Download className="w-3.5 h-3.5" /></a>
                      {canWrite && <button type="button" onClick={() => remove(f)} className={`${btn("nav")} hover:text-red-600`} title="Remove" data-testid={`client-file-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="client-file-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Attach files</DialogTitle>
            <DialogDescription className="text-xs">PDF, images, Word, Excel, CSV or TXT · up to 25 MB each.</DialogDescription>
          </DialogHeader>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => setPicked(Array.from(e.target.files || []))} data-testid="client-file-input" />
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="client-file-pick"
            className="w-full rounded-lg border-2 border-dashed border-slate-300 py-6 text-sm text-slate-500 hover:border-emerald-400 hover:text-emerald-700 flex flex-col items-center gap-1">
            <Upload className="w-5 h-5" />
            {picked.length ? `${picked.length} file${picked.length === 1 ? "" : "s"} selected` : "Click to choose files"}
          </button>
          {picked.length > 0 && <ul className="text-xs text-slate-600 space-y-0.5 max-h-24 overflow-y-auto">{picked.map((f) => <li key={f.name} className="truncate">{f.name} <span className="text-slate-400">· {fmtSize(f.size)}</span></li>)}</ul>}
          <div className="grid grid-cols-2 gap-2">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-9 text-sm" data-testid="client-file-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="h-9 text-sm" title="Expiry (COIs / certificates)" data-testid="client-file-expires" />
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-9 text-sm" data-testid="client-file-note" />
          <div className="flex justify-end gap-2">
            <button type="button" className={btn("nav")} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className={btn("write")} onClick={upload} disabled={busy} data-testid="client-file-submit"><Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
